import { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Spinner, Tire, Snowflake, Sun, Leaf, Check, Storefront, Plus } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function StockModal({ isOpen, onClose, dimension, onSelect, onExternalSelect }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [externalOpen, setExternalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && dimension) {
      fetchStock();
      setExternalOpen(false);
    }
  }, [isOpen, dimension]);

  const fetchStock = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/api/stock`, {
        params: { dimension },
        headers: getAuthHeaders()
      });
      setResults(response.data);
    } catch (err) {
      setError('Stock indisponible - vérifier la connexion');
    } finally {
      setLoading(false);
    }
  };

  const getSeasonIcon = (season) => {
    switch (season) {
      case 'ete': return <Sun size={16} className="text-amber-500" />;
      case 'hiver': return <Snowflake size={16} className="text-blue-500" />;
      case '4saisons': return <Leaf size={16} className="text-green-500" />;
      default: return null;
    }
  };

  const getConditionLabel = (score) => {
    switch (score) {
      case 1: return 'Usé';
      case 2: return 'Correct';
      case 3: return 'Bon';
      case 4: return 'Excellent';
      default: return '-';
    }
  };

  const neufResults = results?.results?.filter(r => r.type === 'neuf') || [];
  const occasionResults = results?.results?.filter(r => r.type === 'occasion') || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Tire size={24} className="text-reco-500" />
            Stock Reco Stock - {dimension}
          </DialogTitle>
          <DialogDescription>
            Sélectionnez un pneu du stock ou choisissez un fournisseur externe
          </DialogDescription>
        </DialogHeader>

        {!externalOpen ? (
          <>
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Spinner size={32} className="animate-spin text-reco-500" />
              </div>
            )}

            {error && (
              <div className="text-center py-8 text-red-500">{error}</div>
            )}

            {results && !loading && (
              <div className="space-y-6">
                {results.mock && (
                  <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                    Données de démonstration (API externe indisponible)
                  </div>
                )}

                {results.results?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Aucun stock disponible pour cette dimension
                  </div>
                ) : (
                  <>
                    {neufResults.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Pneus Neufs</h3>
                        <div className="space-y-2">
                          {neufResults.map((item, idx) => (
                            <StockItem key={idx} item={item} getSeasonIcon={getSeasonIcon} getConditionLabel={getConditionLabel} onSelect={(it) => { onSelect?.(it); onClose(); }} />
                          ))}
                        </div>
                      </div>
                    )}
                    {occasionResults.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Pneus Occasion</h3>
                        <div className="space-y-2">
                          {occasionResults.map((item, idx) => (
                            <StockItem key={idx} item={item} getSeasonIcon={getSeasonIcon} getConditionLabel={getConditionLabel} onSelect={(it) => { onSelect?.(it); onClose(); }} />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* External supplier button */}
            <div className="pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => setExternalOpen(true)}
                className="w-full border-gray-300 hover:bg-gray-50 gap-2"
                data-testid="external-supplier-btn"
              >
                <Storefront size={18} />
                Pneus extérieurs
              </Button>
            </div>
          </>
        ) : (
          <ExternalSupplierPanel
            onSelect={(source) => {
              onExternalSelect?.(source);
              onClose();
            }}
            onBack={() => setExternalOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ExternalSupplierPanel({ onSelect, onBack }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'list' | 'free' | 'autre'
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [freeName, setFreeName] = useState('');
  const [freeAddress, setFreeAddress] = useState('');
  const [savingSupplier, setSavingSupplier] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await axios.get(`${API}/api/suppliers`, { headers: getAuthHeaders() });
        setSuppliers(r.data);
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSelectFromList = () => {
    const s = suppliers.find(s => s.id === selectedSupplier);
    if (s) {
      onSelect(`Fournisseur : ${s.name}${s.address ? ' — ' + s.address : ''}`);
    }
  };

  const handleSelectFree = () => {
    if (!freeName.trim()) { toast.error('Nom du fournisseur requis'); return; }
    onSelect(`Fournisseur : ${freeName.trim()}${freeAddress.trim() ? ' — ' + freeAddress.trim() : ''}`);
  };

  const handleAddToSuppliers = async () => {
    if (!freeName.trim()) return;
    setSavingSupplier(true);
    try {
      await axios.post(`${API}/api/suppliers`, { name: freeName.trim(), address: freeAddress.trim() }, { headers: getAuthHeaders() });
      toast.success('Fournisseur ajouté');
    } catch {
      toast.error('Erreur');
    } finally {
      setSavingSupplier(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Spinner size={24} className="animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-4">
      <Button type="button" variant="ghost" onClick={onBack} className="text-gray-500 -ml-2 text-sm">
        &larr; Retour au stock
      </Button>

      <h3 className="font-semibold text-gray-900">Source des pneus</h3>

      {/* Option A: From supplier list */}
      <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${mode === 'list' ? 'border-reco-500 bg-reco-50' : 'border-gray-200 hover:border-gray-300'}`}
        onClick={() => setMode('list')}>
        <div className="font-medium text-gray-900 mb-2">Fournisseur de la liste</div>
        {mode === 'list' && (
          <div className="space-y-3 mt-3">
            {suppliers.length === 0 ? (
              <p className="text-sm text-gray-500">Aucun fournisseur. Ajoutez-en dans Paramètres → Fournisseurs.</p>
            ) : (
              <>
                <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                  <SelectTrigger className="bg-white border-gray-300" data-testid="supplier-select">
                    <SelectValue placeholder="Sélectionner un fournisseur" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}{s.address ? ` — ${s.address}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" onClick={handleSelectFromList} disabled={!selectedSupplier}
                  className="bg-reco-500 hover:bg-reco-600 text-white w-full" data-testid="select-supplier-btn">
                  Sélectionner
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Option B: Free text */}
      <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${mode === 'free' ? 'border-reco-500 bg-reco-50' : 'border-gray-200 hover:border-gray-300'}`}
        onClick={() => setMode('free')}>
        <div className="font-medium text-gray-900 mb-2">Adresse libre</div>
        {mode === 'free' && (
          <div className="space-y-3 mt-3">
            <div className="space-y-2">
              <Label>Nom du fournisseur</Label>
              <Input value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder="Nom..." className="bg-white border-gray-300" data-testid="free-supplier-name" />
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input value={freeAddress} onChange={(e) => setFreeAddress(e.target.value)} placeholder="Adresse..." className="bg-white border-gray-300" />
            </div>
            <div className="flex gap-2">
              <Button type="button" onClick={handleSelectFree} disabled={!freeName.trim()}
                className="flex-1 bg-reco-500 hover:bg-reco-600 text-white" data-testid="select-free-supplier-btn">
                Sélectionner
              </Button>
              <Button type="button" variant="outline" onClick={handleAddToSuppliers} disabled={!freeName.trim() || savingSupplier}
                className="border-gray-300 gap-1">
                <Plus size={14} /> Ajouter
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Option C: Autre */}
      <div className={`border rounded-lg p-4 cursor-pointer transition-colors ${mode === 'autre' ? 'border-reco-500 bg-reco-50' : 'border-gray-200 hover:border-gray-300'}`}
        onClick={() => setMode('autre')}>
        <div className="font-medium text-gray-900 mb-2">Autre</div>
        {mode === 'autre' && (
          <div className="mt-3">
            <Button type="button" onClick={() => onSelect('Autre')}
              className="bg-reco-500 hover:bg-reco-600 text-white w-full" data-testid="select-autre-btn">
              Confirmer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function StockItem({ item, getSeasonIcon, getConditionLabel, onSelect }) {
  return (
    <div
      className={`bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between ${onSelect ? 'cursor-pointer hover:border-reco-500 hover:bg-reco-50 transition-colors' : ''}`}
      onClick={() => onSelect?.(item)}
    >
      <div className="flex items-center gap-3">
        <div>
          <div className="font-medium text-gray-900">{item.brand} {item.model}</div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
            {getSeasonIcon(item.season)}
            <span className="capitalize">{item.season}</span>
            {item.condition_score && (
              <span className="text-amber-600 font-medium">
                État: {item.condition_score}/4 ({getConditionLabel(item.condition_score)})
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 text-xs">
          {item.runflat && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">RunFlat</span>}
          {item.nv && <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">NV</span>}
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-reco-600">{item.quantity}</div>
          <div className="text-xs text-gray-500">en stock</div>
        </div>
        {onSelect && (
          <Button type="button" size="sm" className="bg-reco-500 hover:bg-reco-600 text-white">
            <Check size={16} className="mr-1" /> Sélectionner
          </Button>
        )}
      </div>
    </div>
  );
}
